import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DoctorLayout from "@/pages/doctor/DoctorLayout";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Search, ArrowUpDown, Archive, ArchiveRestore, X } from "lucide-react";
import { formatDate } from "@/lib/date";
import { toast } from "sonner";

const RISK_FILTER_LABELS = {
  missed_followups: "Missed Follow-ups",
  high_risk: "High-Risk Patients",
  pending_investigations: "Pending Investigations",
  new_this_month: "New Patients This Month",
};

export default function PatientManagementPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [result, setResult] = useState({ patients: [], total: 0 });
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [archived, setArchived] = useState(false);
  const navigate = useNavigate();
  const pageSize = 10;
  const riskFilter = searchParams.get("risk_filter") || "";

  const load = () => {
    const params = { search, sort_by: sortBy, sort_dir: sortDir, archived, page, page_size: pageSize };
    if (riskFilter) params.risk_filter = riskFilter;
    api.get("/patients", { params }).then(({ data }) => setResult(data));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortDir, archived, page, riskFilter]);

  useEffect(() => {
    setPage(1);
  }, [riskFilter]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const archiveToggle = async (p) => {
    try {
      await api.post(`/patients/${p.id}/${p.is_archived ? "unarchive" : "archive"}`);
      toast.success(p.is_archived ? "Patient unarchived" : "Patient archived");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const columns = [
    ["patient_code", "Patient ID"],
    ["full_name", "Name"],
    ["mobile", "Mobile"],
    ["age", "Age"],
    ["created_at", "Registered"],
  ];

  return (
    <DoctorLayout>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-slate-900" data-testid="patient-management-heading">
          Patient Management
        </h1>
        <div className="flex gap-2">
          <Button
            data-testid="toggle-archived-btn"
            variant="outline"
            onClick={() => { setArchived(!archived); setPage(1); }}
            className="gap-2"
          >
            {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
            {archived ? "View Active" : "Archived Patients"}
          </Button>
        </div>
      </div>

      <div className="relative mt-4 max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input
          data-testid="patient-search-input"
          placeholder="Search by name, patient ID, mobile, age, DOB, address..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="pl-9"
        />
      </div>

      {riskFilter && (
        <div className="mt-4 flex items-center gap-2" data-testid="risk-filter-chip">
          <span className="rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-700">
            {RISK_FILTER_LABELS[riskFilter] || riskFilter}
          </span>
          <button data-testid="clear-risk-filter-btn" onClick={() => setSearchParams({})} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
            <X className="h-3 w-3" /> Clear
          </button>
        </div>
      )}

      <div className="mt-4">
        <Table>
          <TableHeader>
            <TableRow className="sticky top-0">
              {columns.map(([field, label]) => (
                <TableHead key={field}>
                  <button
                    data-testid={`sort-${field}`}
                    className="flex items-center gap-1 hover:text-teal-900"
                    onClick={() => toggleSort(field)}
                  >
                    {label} <ArrowUpDown className="h-3 w-3" />
                  </button>
                </TableHead>
              ))}
              <TableHead>Actions</TableHead>
              <TableHead>Flags</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.patients.map((p) => (
              <TableRow key={p.id} data-testid={`patient-row-${p.patient_code}`}>
                <TableCell className="font-mono text-xs">{p.patient_code}</TableCell>
                <TableCell>
                  <button className="font-medium text-teal-700 hover:underline" onClick={() => navigate(`/doctor/patients/${p.id}`)}>
                    {p.full_name}
                  </button>
                </TableCell>
                <TableCell>{p.mobile}</TableCell>
                <TableCell>{p.age ?? "-"}</TableCell>
                <TableCell className="text-slate-600">{formatDate(p.created_at)}</TableCell>
                <TableCell>
                  <button data-testid={`archive-toggle-${p.patient_code}`} onClick={() => archiveToggle(p)} className="text-slate-500 hover:text-teal-700">
                    {p.is_archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                  </button>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1" data-testid={`patient-flags-${p.patient_code}`}>
                    {(p.flags || []).map((f) => (
                      <span key={f.key} title={f.label} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">
                        {f.emoji}
                      </span>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {result.patients.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-slate-400">No patients found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <span data-testid="pagination-info">
          Page {page} of {Math.max(1, Math.ceil(result.total / pageSize))} ({result.total} patients)
        </span>
        <div className="flex gap-2">
          <Button data-testid="pagination-prev" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <Button
            data-testid="pagination-next"
            variant="outline"
            size="sm"
            disabled={page >= Math.ceil(result.total / pageSize)}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </DoctorLayout>
  );
}
